import { isMessageFromCurrentUser, isSameUser } from './reviewChatItems';
import type { FileReviewThreadChangedNotice } from './realtime/fileReviewDiscussionTypes';
import type { FileReviewAnchor, FileReviewMessage, FileReviewThreadDto } from './types';

export interface DiscussionPeerActivityEvent {
  changeType: string;
  changedByDisplayName: string;
  changedByUserId: string | null;
  changedAtUtc: string;
  threadId: string;
  mountId: string;
  path: string;
  messageIds?: string[];
  anchorIds?: string[];
}

interface PeerThreadDiff {
  inferredChangeType: string;
  changedByUserId: string | null;
  changedByDisplayName: string;
  messageIds: string[];
  anchorIds: string[];
}

function isActorCurrentUser(
  actorId: string | null | undefined,
  currentUserId: string,
  aliasSet: ReadonlySet<string>
): boolean {
  if (!actorId?.trim()) return false;
  if (isSameUser(actorId.trim(), currentUserId)) return true;
  for (const alias of aliasSet) {
    if (isSameUser(actorId.trim(), alias)) return true;
  }
  return false;
}

function isAnchorFromCurrentUser(
  anchor: FileReviewAnchor,
  currentUserId: string,
  aliasSet: ReadonlySet<string>
): boolean {
  const createdBy = anchor.createdBy?.trim();
  if (!createdBy) return false;
  if (isSameUser(createdBy, currentUserId)) return true;
  for (const alias of aliasSet) {
    if (isSameUser(createdBy, alias)) return true;
  }
  return false;
}

function formatMessageAuthorName(message: FileReviewMessage): string {
  return (
    message.authorDisplayName?.trim() ||
    message.submittedByDisplayName?.trim() ||
    message.submittedBy?.trim() ||
    'Someone'
  );
}

function formatAnchorAuthorName(anchor: FileReviewAnchor): string {
  return anchor.createdByDisplayName?.trim() || anchor.createdBy?.trim() || 'Someone';
}

function diffPeerThreadChanges(
  previousThread: FileReviewThreadDto,
  nextThread: FileReviewThreadDto,
  currentUserId: string,
  aliasSet: ReadonlySet<string>
): PeerThreadDiff | null {
  const prevMsgMap = new Map(previousThread.messages.map((m) => [m.id, m]));
  const nextMsgIds = new Set(nextThread.messages.map((m) => m.id));
  const prevAnchorIds = new Set(previousThread.anchors.map((a) => a.id));
  const nextAnchorIds = new Set(nextThread.anchors.map((a) => a.id));

  const newMessages = nextThread.messages.filter(
    (m) => !prevMsgMap.has(m.id) && !isMessageFromCurrentUser(m, currentUserId, aliasSet)
  );
  const updatedMessages = nextThread.messages.filter((m) => {
    const prev = prevMsgMap.get(m.id);
    if (!prev) return false;
    if (prev.text === m.text && prev.updatedAt === m.updatedAt) return false;
    return !isMessageFromCurrentUser(m, currentUserId, aliasSet);
  });
  const deletedMessages = previousThread.messages.filter(
    (m) => !nextMsgIds.has(m.id) && !isMessageFromCurrentUser(m, currentUserId, aliasSet)
  );

  const newAnchors = nextThread.anchors.filter(
    (a) => !prevAnchorIds.has(a.id) && !isAnchorFromCurrentUser(a, currentUserId, aliasSet)
  );
  const deletedAnchors = previousThread.anchors.filter(
    (a) => !nextAnchorIds.has(a.id) && !isAnchorFromCurrentUser(a, currentUserId, aliasSet)
  );

  if (
    !newMessages.length &&
    !updatedMessages.length &&
    !deletedMessages.length &&
    !newAnchors.length &&
    !deletedAnchors.length
  ) {
    return null;
  }

  let inferredChangeType = 'message-created';
  let primaryMessage: FileReviewMessage | undefined;
  let primaryAnchor: FileReviewAnchor | undefined;

  if (newMessages.length) {
    inferredChangeType = 'message-created';
    primaryMessage = newMessages[newMessages.length - 1];
  } else if (newAnchors.length) {
    inferredChangeType = 'anchor-created';
    primaryAnchor = newAnchors[newAnchors.length - 1];
  } else if (updatedMessages.length) {
    inferredChangeType = 'message-updated';
    primaryMessage = updatedMessages[updatedMessages.length - 1];
  } else if (deletedMessages.length) {
    inferredChangeType = 'message-deleted';
    primaryMessage = deletedMessages[deletedMessages.length - 1];
  } else if (deletedAnchors.length) {
    inferredChangeType = 'anchor-deleted';
    primaryAnchor = deletedAnchors[deletedAnchors.length - 1];
  }

  const changedByUserId =
    primaryMessage?.submittedByUserId?.trim() ||
    primaryMessage?.submittedBy?.trim() ||
    primaryAnchor?.createdBy?.trim() ||
    null;
  const changedByDisplayName = primaryMessage
    ? formatMessageAuthorName(primaryMessage)
    : primaryAnchor
      ? formatAnchorAuthorName(primaryAnchor)
      : 'Someone';

  return {
    inferredChangeType,
    changedByUserId,
    changedByDisplayName,
    messageIds: [
      ...newMessages.map((m) => m.id),
      ...updatedMessages.map((m) => m.id),
      ...deletedMessages.map((m) => m.id)
    ],
    anchorIds: [...newAnchors.map((a) => a.id), ...deletedAnchors.map((a) => a.id)]
  };
}

/**
 * Detects peer discussion activity after a thread reload. Returns null on initial load or when the
 * change was made by the current user.
 */
export function discoverPeerThreadActivity(
  previousThread: FileReviewThreadDto | null,
  nextThread: FileReviewThreadDto,
  notice: FileReviewThreadChangedNotice | null,
  currentUserId: string,
  currentUserAliases: Iterable<string>
): DiscussionPeerActivityEvent | null {
  if (!previousThread) return null;

  const aliasSet = new Set(currentUserAliases);
  if (isActorCurrentUser(notice?.changedByUserId, currentUserId, aliasSet)) {
    return null;
  }

  const diff = diffPeerThreadChanges(previousThread, nextThread, currentUserId, aliasSet);
  if (!diff) return null;

  return {
    changeType: notice?.changeType?.trim() || diff.inferredChangeType,
    changedByDisplayName: notice?.changedByDisplayName?.trim() || diff.changedByDisplayName,
    changedByUserId: notice?.changedByUserId ?? diff.changedByUserId,
    changedAtUtc: notice?.changedAtUtc ?? new Date().toISOString(),
    threadId: nextThread.threadId,
    mountId: nextThread.mountId,
    path: nextThread.path,
    messageIds: diff.messageIds.length ? diff.messageIds : undefined,
    anchorIds: diff.anchorIds.length ? diff.anchorIds : undefined
  };
}
